import React from 'react';
import { CheckCircle, Home, Users, Award, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import AllPetCardSection from '../components/AllPetCardSection';

const BoardAndTrains = () => {
  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const features = [
    'Our 3-6 Week Board and Train program is tailored to dogs in farm and ranch settings.',
    'Staying in the trainer\'s home, your dog will enjoy daily barn visits, gain essential skills, learn obedience commands, and socialize at local stores and restaurants.',
    'Our unique approach allows dogs to go home on a weekend with a lesson, allowing owners to practice what they\'ve learned while benefiting from one-on-one guidance throughout the program.',
    'Once the program is completed, we offer 4 free lessons, lifetime discounts, referral bonuses, and lifetime support, ensuring you and your dog stay on the right track!'
  ];

  const benefits = [
    {
      icon: <Home size={32} />,
      title: 'Home Environment',
      description: 'Your dog stays in our home, not a kennel, becoming part of the family'
    },
    {
      icon: <Users size={32} />,
      title: 'Weekend Visits',
      description: 'Practice skills at home with expert guidance throughout the program'
    },
    {
      icon: <Award size={32} />,
      title: 'Lifetime Support',
      description: '4 free lessons, lifetime discounts, referral bonuses, and ongoing support'
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Board & Train Dog Programs — Farm Dog Oklahoma | Tulsa, Broken Arrow, Owasso OK</title>
        <meta name="description" content="Immersive 3-6 week board and train dog programs in Tulsa, Oklahoma. Your dog lives in the trainer's home with daily barn time, real-world training, and farm exposure. Serving Broken Arrow, Owasso, Jenks, Bixby, and surrounding cities." />
        <link rel="canonical" href="https://farmdogoklahoma.com/board-and-trains" />
        <meta property="og:title" content="Board & Train Dog Programs — Farm Dog Oklahoma" />
        <meta property="og:description" content="Immersive 3-6 week board and train programs. Your dog lives in the trainer's home with real-world farm training." />
        <meta property="og:url" content="https://farmdogoklahoma.com/board-and-trains" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#5C4033] to-[#3E2723] py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#C4A052] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#C4A052] rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Board and Trains
            </h1>
            <p className="text-xl md:text-2xl text-[#C4A052] font-semibold">
              Real-World Training for Real-Life Results
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <motion.section 
        className="section-padding bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeInUp}
      >
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div variants={fadeInUp}>
              <img 
                src="/assets/images/1CK1C1D31hHrT2egbyPKC-mkHinzBpi4h.jpg" 
                alt="Dog training on farm" 
                className="rounded-2xl shadow-2xl w-full h-96 object-cover object-center"
              />
            </motion.div>
            <motion.div className="space-y-6" variants={fadeInUp}>
              <h2 className="text-4xl font-bold text-[#5C4033]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Our Immersive Board & Train Program
              </h2>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="text-[#C4A052] flex-shrink-0 mt-1" size={24} />
                    <p className="text-gray-700 leading-relaxed">{feature}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Benefits Grid */}
          <motion.div 
            className="grid md:grid-cols-3 gap-8 mb-16"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {benefits.map((benefit, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="hover-lift border-none shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#C4A052] to-[#B8935E] rounded-full text-white mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-bold text-[#5C4033] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {benefit.title}
                  </h3>
                  <p className="text-gray-700">{benefit.description}</p>
                </CardContent>
              </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Program Details */}
          <motion.div 
            className="bg-[#F5F5F0] rounded-2xl p-8 md:p-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h3 className="text-3xl font-bold text-[#5C4033] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              What's Included
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#C4A052] rounded-full"></div>
                  <span className="text-gray-700">Daily barn visits and farm exposure</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#C4A052] rounded-full"></div>
                  <span className="text-gray-700">Essential obedience commands</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#C4A052] rounded-full"></div>
                  <span className="text-gray-700">Socialization at local stores & restaurants</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#C4A052] rounded-full"></div>
                  <span className="text-gray-700">Living in trainer's home (not a kennel)</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#C4A052] rounded-full"></div>
                  <span className="text-gray-700">Weekend home visits with lessons</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#C4A052] rounded-full"></div>
                  <span className="text-gray-700">One-on-one guidance throughout</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#C4A052] rounded-full"></div>
                  <span className="text-gray-700">4 free lessons after completion</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#C4A052] rounded-full"></div>
                  <span className="text-gray-700">Lifetime support & discounts</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* All Pet Card Section */}
      <AllPetCardSection />

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-[#C4A052] to-[#B8935E] py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Book a free consultation today and let's discuss your dog's training journey!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="/contact"
            >
              <Button className="bg-white text-[#5C4033] hover:bg-gray-100 font-semibold px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:shadow-xl">
                Book Free Consult <ArrowRight className="ml-2" />
              </Button>
            </a>
            <a href="tel:+19189927332">
              <Button className="bg-[#5C4033] text-white hover:bg-[#3E2723] font-semibold px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:shadow-xl">
                Call (918) 992-7332
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BoardAndTrains;